import { ThemeEvents } from '@theme/events';

/**
 * Sticky mobile add-to-cart bar.
 * - Becomes visible once its `data-target` element (the main buy-buttons module) has scrolled
 *   fully above the viewport, and hides again once that module is back on screen.
 * - Keeps its button's enabled/disabled state and label in sync with the real variant picker by
 *   listening for the theme's `variant:update` event, since the bar's button lives outside the
 *   product form and is only linked to it via the `form` attribute.
 */
class StickyCartBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const targetId = this.dataset.target;
    const target = targetId ? document.getElementById(targetId) : null;

    if (target && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.dataset.visible = scrolledPast ? 'true' : 'false';
          }
        },
        { threshold: 0 }
      );
      this.#observer.observe(target);
    }

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller.abort();
  }

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const productId = this.dataset.productId;
    const eventProductId = event.detail?.data?.productId;

    if (productId && eventProductId && String(eventProductId) !== String(productId)) return;

    const variant = event.detail?.resource;
    if (!variant) return;

    const button = this.querySelector('[ref="stickyAddToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = !variant.available;

    const textEl = button.querySelector('.sticky-cart-bar__button-text');
    if (textEl) {
      textEl.textContent = variant.available ? this.dataset.addToCartText : this.dataset.soldOutText;
    }
  };
}

if (!customElements.get('sticky-cart-bar')) {
  customElements.define('sticky-cart-bar', StickyCartBar);
}
